"use client";
// Imports
import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  Container,
  RowCard,
  FilterBar,
  Pagination,
  ComponentWrapper,
} from "@/components";
import { refineResults } from "@/lib/helpers";
import useStore from "@/lib/state";
import { useKindeSession } from "@/components/KindeWrapper";

// Showbags Grid
export function RowGrid({
  type,
  showFilter,
  showPagination,
  gridData,
  collectionHandle,
  categories,
  competitionTags = [],
  mapIsActive,
}) {
  const { user } = useKindeSession();
  const userId = user?.id || null;

  // Variables
  const searchParams = useSearchParams();
  const {
    sortBy,
    searchTerm,
    activeCategories,
    currentPage,
    pageBy,
    setSortBy,
    setSearchTerm,
    setActiveCategories,
    setCurrentPage,
    setPageBy,
    selectedDay,
  } = useStore();

  const [activeCompetitionTags, setActiveCompetitionTags] = useState([]);

  // Set State from URL Params
  useEffect(() => {
    setSortBy(searchParams.get("sortBy") ? searchParams.get("sortBy") : "a-z");
    setSearchTerm(
      searchParams.get("searchTerm") ? searchParams.get("searchTerm") : ""
    );
    setActiveCategories(
      searchParams.get("activeCategories")?.split(",")
        ? searchParams.get("activeCategories")?.split(",")
        : []
    );
    setCurrentPage(
      parseInt(searchParams.get("currentPage"))
        ? parseInt(searchParams.get("currentPage"))
        : 1
    );
    setPageBy(searchParams.get("pageBy") ? searchParams.get("pageBy") : 24);
  }, [searchParams]);

  // Refine Results
  const refinedResults = refineResults(
    gridData,
    sortBy,
    searchTerm,
    activeCategories,
    pageBy,
    currentPage,
    collectionHandle,
    undefined,
    undefined,
    activeCompetitionTags
  );
  const totalResults = refinedResults?.flat().length;
  const startAmount = (currentPage - 1) * pageBy + 1;
  const endAmount = Math.min(
    currentPage * pageBy,
    refinedResults?.flat().length
  );

  // useEffect to push back to page 1 if current page exceeds the new total amount of <results></results>
  useEffect(() => {
    const totalResults = refinedResults?.flat().length;
    const totalPages = Math.ceil(totalResults / pageBy);
    if (currentPage > totalPages && totalResults > 0) {
      setCurrentPage(1);
    }
  }, [refinedResults, currentPage, pageBy, setCurrentPage]);

  // Render
  return (
    <ComponentWrapper type={type}>
      <Container>
        {showFilter && (
          <FilterBar
            collectionHandle={collectionHandle}
            categories={categories}
            searchParams={searchParams}
            setSortBy={setSortBy}
            setSearchTerm={setSearchTerm}
            setActiveCategories={setActiveCategories}
            activeCategories={activeCategories}
            tagItems={competitionTags}
            setActiveTags={setActiveCompetitionTags}
            activeTags={activeCompetitionTags}
          />
        )}
        <div
          className="grid grid-cols-1 gap-4"
          key={[
            collectionHandle,
            currentPage,
            sortBy,
            searchTerm,
            activeCategories.join(","),
            activeCompetitionTags.join(","),
            selectedDay?.slug,
          ].join("|")}
        >
          {refinedResults[currentPage - 1]?.length > 0 ? (
            refinedResults[currentPage - 1]?.map((page, index) => (
              <RowCard
                mapIsActive={mapIsActive}
                userId={userId}
                collectionHandle={collectionHandle}
                key={page.id ?? page.slug ?? index}
                index={index}
                data={page}
                image={page.image}
                selectedDay={selectedDay}
                url={`${collectionHandle}/${page?.slug}`}
              />
            ))
          ) : (
            <div className="flex flex-col col-span-4 justify-center items-center p-12 space-y-4 w-full h-full">
              <h1>No Results</h1>
              <p>Try adjusting your search</p>
            </div>
          )}
        </div>
        {showPagination && (
          <Pagination
            isResultMoreThenPage={
              refinedResults?.flat().length - currentPage * pageBy <
              parseInt(pageBy) / 2
            }
            searchParams={searchParams}
          />
        )}
      </Container>
    </ComponentWrapper>
  );
}
